import type { Issue, QuickWinType, StatusChange, Type } from "."

// ISSUE TYPES
export const isVulnerableCodeType = (issueType: Type) =>
  issueType === "Vulnerable_Code"

export const isExposedSecretType = (issueType: Type) =>
  issueType === "Exposed_Secret"

export const isCodeIssueType = (issueType: Type) =>
  isVulnerableCodeType(issueType) || isExposedSecretType(issueType)

export const isCodeIssue = (issue: Issue) => isCodeIssueType(issue.issueType)

export const isMaliciousPackageType = (issueType: Type) =>
  issueType === "Malicious_Package"

export const isMaliciousPackageIssue = (issue: Issue) =>
  isMaliciousPackageType(issue?.issueType)

export const isVulnerablePackageType = (issueType: Type) =>
  issueType === "Vulnerable_Package"

export { getColorBySeverity } from "../vulnerability"

export const isPackageIssueType = (issueType: Type) =>
  issueType === "Vulnerable_Package"

//LANGUAGE
export const getLanguage = (issue: Pick<Issue, "language">) => issue.language

// RELATED APPLICATIONS
export const getRelatedApplications = (issue: Issue) =>
  issue.relatedApplications ?? []

export const getRelatedApplicationsCount = (issue: Issue) =>
  getRelatedApplications(issue).length

// RISK INSIGHTS
export const getRiskInsights = (issue: Pick<Issue, "riskInsights">) =>
  issue.riskInsights || {}

// RUNTIME - RISK INSIGHTS
export const isRuntime = (issue: Pick<Issue, "riskInsights">) =>
  !!getRiskInsights(issue).runtime

export const isExecuted = (issue: Pick<Issue, "riskInsights">) =>
  getRiskInsights(issue).executed === "YES"

// INTERNET FACING - RISK INSIGHTS
export const getInternetFacing = (issue: Issue) =>
  getRiskInsights(issue).internetFacing

export const isInternetFacingUndetermined = (issue: Issue) =>
  getInternetFacing(issue) === "UNDETERMINED"

export const isInternetFacingDetermined = (issue: Issue) =>
  !isInternetFacingUndetermined(issue)

// INGRESS - RISK INSIGHTS
export const isIngressTrue = (issue: Issue) => !!getRiskInsights(issue).ingress

// EXPLOIT MATURITY - RISK INSIGHTS
export const getExploitMaturity = (issue: Issue) =>
  getRiskInsights(issue).exploitMaturity

export const isExploitMaturityInTheWild = (issue: Issue) =>
  getExploitMaturity(issue) === "IN_THE_WILD"

export const isExploitMaturityPOC = (issue: Issue) =>
  getExploitMaturity(issue) === "POC"

export const isExploitMaturityUndetermined = (issue: Issue) =>
  getExploitMaturity(issue) === "UNDETERMINED"

export const isExploitMaturityDetermined = (issue: Issue) =>
  !isExploitMaturityUndetermined(issue)

export const isIssueDismissed = (
  issue: Pick<Issue, "status" | "dismissMethod">,
  checkMethod: boolean = true
) =>
  (issue.status === "dismissed" || issue.status === "dismissed_resolved") &&
  (checkMethod ? !!issue.dismissMethod : true)

export const isIssueResolved = (
  issue: Partial<Pick<Issue, "status" | "isResolved">>
) =>
  issue.status === "resolved" ||
  issue.status === "dismissed_resolved" ||
  issue.isResolved === true

export const isResolvedInCodeRepo = (
  issue: Partial<Pick<Issue, "resolutionData">>
) => !!issue.resolutionData?.isResolvedInCodeRepo

export const isResolvedInCodeRepoAndNotResolved = (
  issue: Partial<Pick<Issue, "status" | "isResolved" | "resolutionData">>
) => !isIssueResolved(issue) && isResolvedInCodeRepo(issue)

export const isIssueProcessing = (issue: Pick<Issue, "status">) =>
  issue.status === "processing"

export const isDismissedAndResolved = (
  issue: Pick<Issue, "status" | "dismissMethod" | "isResolved">
) => isIssueDismissed(issue, false) && isIssueResolved(issue)

// Resolved in the current scope (isResolved) and not dismissed: a dismissed one is dismissed and resolved
export const isFullyResolvedNotDismissed = (
  issue: Pick<Issue, "status" | "dismissMethod" | "isResolved">
) => issue.isResolved === true && !isIssueDismissed(issue, false)

// kosmos-api returns the issue's latest dismiss or undismiss in dismiss*: on an open issue,
// it is the undismiss.
export const getLastStatusChange = (
  issue: Partial<
    Pick<
      Issue,
      | "status"
      | "dismissedAt"
      | "dismissedBy"
      | "dismissReason"
      | "dismissMethod"
    >
  >
): StatusChange | null => {
  const {
    status,
    dismissedAt,
    dismissedBy = "",
    dismissReason = "",
    dismissMethod = ""
  } = issue

  if (!dismissedAt) return null

  const isDismissed = status === "dismissed" || status === "dismissed_resolved"

  if (!isDismissed && status !== "open") return null

  return {
    from: isDismissed ? "open" : "dismissed",
    to: isDismissed ? "dismissed" : "open",
    at: dismissedAt,
    by: dismissedBy,
    method: dismissMethod,
    reason: dismissReason
  }
}

// Quick Win helpers (merged from domain/quickWin)

export const isPackageUpdate = (type: QuickWinType) => type === "PACKAGE_UPDATE"

export const isBaseImageUpdate = (type: QuickWinType) =>
  type === "BASE_IMAGE_UPDATE"

/** Maps backend updateType integer to a label key suffix.
 *  Values match QuickWinUpdateType proto enum in api-inventory. */
export const UPDATE_TYPE = {
  MINOR: 1,
  PATCH: 6
} as const

export const UPDATE_TYPE_LABEL: Record<number, string> = {
  [UPDATE_TYPE.PATCH]: "Patch",
  [UPDATE_TYPE.MINOR]: "Minor"
}

export const getUpdateTypeLabel = (updateType: number) =>
  UPDATE_TYPE_LABEL[updateType] ?? ""

export const isHighProfileIssue = (issue: Pick<Issue, "highProfile">) =>
  !!issue.highProfile?.length
